import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import colors from 'constants/colors';
import { COMPANIES } from 'constants/Entities';
import ListIcon from 'components/SvgIcons/List';
import AddEntityDialog from 'containers/components/dialog/AddEntityDialog';
import {
    WidgetContentLayout,
    WidgetEmpty,
    WidgetHeaderLayout,
    WidgetLayout,
} from 'containers/components/widgets/Layouts';
import Context from 'managers/Context';
import { CompanyDiscountsModel } from 'models';
import { getLiteral } from 'utils/getLiteral';
import { normalizeDecimalValue } from 'utils/decimal';

import FamilyDiscountsAddDialogDecorator from './components/decorators/FamilyDiscountsAddDialogDecorator';
import Table from './components/table/DiscountsTable';

import './styles.scss';

const FamilyAddDialogDecorated = FamilyDiscountsAddDialogDecorator(AddEntityDialog);

const CompanyDiscountsCard = memo(({ containerWidth, id, entity }) => {
    // ========================================
    // LOCAL STATE
    // ========================================
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const getList = useCallback(() => {
        Context.entityManager.getEntitiesManager(COMPANIES).getCompanyDiscounts(
            id,
            (response) => {
                const newData =
                    response?.map((discount) => new CompanyDiscountsModel(discount)) || [];
                setData(newData);
                setLoading(false);
            },
            () => {
                setLoading(false);
            },
        );
    }, [id]);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const onDeleteRow = useCallback(
        (rowId) => {
            const index = data.findIndex((row) => row.id === rowId);
            const newData = [...data];
            newData.splice(index, 1);

            Context.entityManager.getEntitiesManager(COMPANIES).deleteCompanyDiscounts(
                rowId,
                () => {
                    setData(newData);
                },
                () => {},
            );
        },
        [data],
    );

    const onChange = useCallback(
        (field, row, index, value) => {
            if (field) {
                const newValue = normalizeDecimalValue(value) ?? 0;
                if (row[field] !== newValue) {
                    row[field] = newValue;
                    const newModel = new CompanyDiscountsModel(row);
                    Context.entityManager.getEntitiesManager(COMPANIES).setCompanyDiscounts(
                        [newModel.saveUpdate],
                        () => {
                            const newData = [...data];
                            newData[index] = newModel;
                            setData(newData);
                        },
                        () => {},
                    );
                }
            } else {
                onDeleteRow(row.id);
            }
        },
        [data, onDeleteRow],
    );

    const onAdd = useCallback(
        (newLine) => {
            const newData = CompanyDiscountsModel.fromAdd(id, newLine.id);
            Context.entityManager.getEntitiesManager(COMPANIES).setCompanyDiscounts(
                [newData],
                () => {
                    getList();
                },
                () => {},
            );
        },
        [getList, id],
    );

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        if (id) {
            getList();
        }
    }, [getList, id]);

    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [containerWidth]);

    // ========================================
    // DERIVED STATE
    // ========================================
    const idEnvironment = entity?.idEnvironment;
    const idCurrency = entity?.idCurrency;
    const tableData = useMemo(() => data.map((row) => row.toMap().toJS()), [data]);

    // ========================================
    // RENDER
    // ========================================
    return (
        <WidgetLayout className="company-discounts-widget-container" data={id} loading={loading}>
            <WidgetHeaderLayout
                content={[
                    {
                        count: data.length || '0',
                        title: getLiteral('title_family_discounts'),
                    },
                ]}
                onClickAdd={handleOpen}
            />
            <WidgetContentLayout>
                <div className="fm-widget-discounts__content">
                    {data.length === 0 ? (
                        <WidgetEmpty
                            icon={<ListIcon color={colors.grey[100]} />}
                            text={getLiteral('error_nodiscountsfound')}
                        />
                    ) : (
                        <div className="content-widget">
                            <Table
                                list={tableData}
                                onChange={onChange}
                                idEnvironment={idEnvironment}
                                idCurrency={idCurrency}
                            />
                        </div>
                    )}
                </div>
                <FamilyAddDialogDecorated
                    entity={null}
                    familiesList={data}
                    handleClose={handleClose}
                    onChange={onAdd}
                    open={open}
                    title={getLiteral('action_search_families')}
                />
            </WidgetContentLayout>
        </WidgetLayout>
    );
});

CompanyDiscountsCard.displayName = 'CompanyDiscountsCard';

export default CompanyDiscountsCard;
